import { useEffect, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Temporal } from "@js-temporal/polyfill";
import { get30DayRunstatusAlarmHistoryForAsset } from "@/api/alarm-api.js";
import {
  DAYS_30_IN_HOURS,
  formatStartTime,
  formatTimeBeforeShutdown,
} from "@/lib/datetime-utils.js";
import { Badge } from "@/components/ui/badge.jsx";
import { LoadingSkeleton } from "@/components/ui/loading.jsx";
import {
  getStatusBadgeClass,
  getStatusDotClass,
} from "@/lib/status-utils.js";

/**
 * Alarm history for the selected shutdown (30-day lookback). Fetches when a shutdown row is selected.
 * @param {{
 *   assetId: string|number,
 *   reportingDate: string|null,
 *   resultsLimit: number,
 * }} props - `reportingDate` is ISO from the selected shutdown row when set.
 * @returns {JSX.Element}
 */
export default function AlarmHistoryList({
  assetId,
  reportingDate,
  resultsLimit,
}) {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));

  useEffect(() => {
    if (
      assetId == null
      || assetId === ""
      || reportingDate == null
      || reportingDate === ""
    ) {
      setRows([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await get30DayRunstatusAlarmHistoryForAsset(
          assetId,
          reportingDate
        );
        if (!cancelled) {
          setRows(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("get30DayRunstatusAlarmHistoryForAsset failed:", err);
          setRows([]);
          setError("Failed to load alarm history");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [assetId, reportingDate]);

  const sortedRows = useMemo(() => {
    return [...rows].sort(
      (a, b) =>
        new Date(b.reportingDate).getTime()
        - new Date(a.reportingDate).getTime()
    );
  }, [rows]);

  const shutdownMs = useMemo(() => {
    if (!reportingDate) return NaN;
    const t = new Date(reportingDate).getTime();
    return Number.isNaN(t) ? NaN : t;
  }, [reportingDate]);

  const alarmCount = sortedRows.length;
  const hasSelection = reportingDate != null && reportingDate !== "";

  const showResultsLimitNote = useMemo(() => {
    if (
      !hasSelection
      || isLoading
      || error
      || sortedRows.length < resultsLimit
      || Number.isNaN(shutdownMs)
    ) {
      return false;
    }
    const oldestMs = new Date(
      sortedRows[sortedRows.length - 1].reportingDate
    ).getTime();
    if (Number.isNaN(oldestMs)) return false;
    const windowStartMs = Temporal.Instant.from(
      new Date(shutdownMs).toISOString()
    ).subtract(DAYS_30_IN_HOURS).epochMilliseconds;
    // Cap hit truncated the window when the oldest row is still inside the 30-day lookback.
    return oldestMs > windowStartMs;
  }, [hasSelection, isLoading, error, sortedRows, shutdownMs, resultsLimit]);

  return (
    <div className='flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card'>
      <div className='shrink-0 border-b border-border px-3 py-3 sm:px-4'>
        <div className='flex items-start gap-2'>
          <AlertTriangle
            className='mt-0.5 h-5 w-5 shrink-0 text-orange-500 dark:text-orange-400'
            aria-hidden
          />
          <div className='min-w-0'>
            <h2 className='text-base font-semibold leading-tight text-foreground'>
              Alarm History - 30 Days
            </h2>
            <p className='mt-1 text-xs text-muted-foreground'>
              {hasSelection
                ? `${alarmCount} alarm${alarmCount === 1 ? "" : "s"} leading up to shutdown — most recent first`
                : "Select a shutdown event to load alarms"}
            </p>
            {showResultsLimitNote ? (
              <p className='mt-1 text-xs text-amber-800 dark:text-amber-300'>
                This unit had a lot of alarms. Only the most recent{" "}
                {resultsLimit} prior to the shutdown are shown.
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className='flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-muted/50'>
        <div className='flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-6 sm:px-4 sm:py-4'>
          {!hasSelection ? (
            <div className='flex shrink-0 flex-col'>
              <p className='text-sm text-muted-foreground'>
                Click a shutdown on the left to see run-status alarms from the
                prior 30 days.
              </p>
            </div>
          ) : null}

          {hasSelection && isLoading ? (
            <div className='flex min-h-0 flex-1 flex-col'>
              <LoadingSkeleton className='min-h-0 w-full flex-1 rounded-md' />
            </div>
          ) : null}

          {hasSelection && !isLoading && error ? (
            <div className='flex shrink-0 flex-col'>
              <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>
            </div>
          ) : null}

          {hasSelection && !isLoading && !error && sortedRows.length === 0 ? (
            <div className='flex shrink-0 flex-col'>
              <p className='text-sm text-muted-foreground'>
                No alarms in this window.
              </p>
            </div>
          ) : null}

          {hasSelection && !isLoading && !error && sortedRows.length > 0 ? (
            <table className='w-full min-w-0 shrink-0 border-collapse text-left text-sm'>
              <tbody className='divide-y divide-border bg-card'>
                {sortedRows.map((row, index) => {
                  const alarmMs = new Date(row.reportingDate).getTime();
                  const isNewestSlot = index === 0;
                  const pillLabel = isNewestSlot
                    ? "at event"
                    : Number.isNaN(shutdownMs) || Number.isNaN(alarmMs)
                      ? "—"
                      : formatTimeBeforeShutdown(shutdownMs, alarmMs, "—");

                  return (
                    <tr
                      key={
                        row.uniqueId ? String(row.uniqueId) : `alarm-${index}`
                      }>
                      <td className='w-px whitespace-nowrap py-2.5 pl-2.5 pr-3 align-middle text-xs font-medium tabular-nums text-foreground'>
                        {formatStartTime(row.reportingDate, "—")}
                      </td>
                      <td className='w-px py-2.5 pl-2 pr-3 align-middle text-center'>
                        <Badge
                          variant='status'
                          className={getStatusBadgeClass(
                            "danger",
                            "w-[5.25rem] shrink-0 justify-center px-2 py-0.5 text-[10px] tabular-nums leading-tight"
                          )}>
                          <span
                            className={getStatusDotClass(
                              "danger",
                              "h-1.5 w-1.5"
                            )}
                            aria-hidden='true'
                          />
                          {pillLabel}
                        </Badge>
                      </td>
                      <td className='min-w-0 py-2.5 pr-2.5 align-middle text-sm leading-snug text-foreground sm:px-3 sm:pr-4'>
                        <p className='break-words line-clamp-2'>
                          {row.message.trim() || "—"}
                        </p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </div>
      </div>
    </div>
  );
}
